import React, {
  useState,
  ForwardRefRenderFunction,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useCallback,
} from "react";

import { ButtonProps, IconButton } from "@mui/material";
import { Drawer } from "@mui/material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { RiCloseLine } from "react-icons/ri";

import { Job } from "../../../models/job";
import { Sales } from "../../../models/sales";
import Button, { Color } from "../../Form/Button";
import EmptySection from "./EmptySection";
import useStyles, { Separator } from "./styles";

export enum Navigation {
  ACCESS = "Access",
  ACTIVITY = "Activity",
  COMMENTS = "Comments",
  DATE = "Date",
  DETAILS = "Details",
  DOCUMENTS = "Documents",
  // ESTIMATE = "Estimate", // TODO Camila: discomment when estimate is ready
  FOLDERS = "Folders",
  INSTRUCTIONS = "Instructions",
  INVENTORY = "Inventory",
  MATERIAL = "Material",
  ORDERS = "Orders",
  SHIPMENT = "Shipment",
  SUMMARY = "Summary",
  TASKS = "Tasks",
  TRACKING = "Tracking",
  VIDEO_SURVEY = "Video Survey",
  VOYAGE_MAP = "Voyage & Map",
}

type Tabs = {
  key: string;
  loading?: boolean;
  isEmpty?: boolean;
  isOpened?: boolean;
  render: () => React.ReactChild;
  renderLoading?: () => React.ReactChild;
};

type MenuItem = {
  key: string;
};

interface ButtonItem extends Omit<ButtonProps, "color"> {
  buttonLabel: string;
  classLabel?: string;
  titlePage?: string;
  class?: string;
  tooltip?: string;
  icon: React.ReactChild;
  color: Color;
  disabled?: boolean;
  renderLoading?: () => React.ReactChild;
  renderComponent?: () => React.ReactChild;
  action?: () => void;
}

interface Props {
  title: string;
  isLoading: boolean;
  tabs: Tabs[];
  navigation: MenuItem[];
  buttonsComponent?: ButtonItem[];
  job?: Job & Sales & any;
  setOpenModal?: (value: boolean) => void;
  onClose?: () => void;
  activityButton?: ButtonItem;
}

export interface PropHandles {
  isOpen?: boolean;
  setOpenSideBar: (value: boolean) => void;
  setCurrentLink: (value: string) => void;
}

const SideViewComponent: ForwardRefRenderFunction<PropHandles, Props> = (props, ref) => {
  const {
    tabs,
    isLoading,
    navigation,
    title,
    setOpenModal,
    buttonsComponent,
    onClose,
    activityButton,
  } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [openSideBar, setOpenSideBar] = useState<boolean>(false);
  const [component, setComponent] = useState<ButtonItem>();
  const [currentLink, setCurrentLink] = useState<string>(Navigation.DETAILS);

  const handleScrollIntoView = (id: string) => {
    const element = document.getElementById(id);

    setCurrentLink(id);

    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleWheel = useCallback(
    (event) => {
      let nextIndex;

      if (event.wheelDelta <= 0) {
        nextIndex = navigation.findIndex((nav) => nav.key === currentLink) + 1;
      } else {
        nextIndex = navigation.findIndex((nav) => nav.key === currentLink) - 1;
      }

      if (nextIndex !== undefined && nextIndex <= navigation.length - 1 && nextIndex >= 0) {
        const nextPage = navigation[nextIndex]?.key;
        const nextPageTop = document?.getElementById(nextPage)?.getBoundingClientRect()?.top;
        const isToShow =
          nextPageTop && event.wheelDelta <= 0
            ? nextPageTop <= 200
            : nextPageTop && nextPageTop >= 95;

        if (nextPageTop && isToShow) setCurrentLink(nextPage);
      }
    },
    [currentLink]
  );

  const renderSection = (tab: Tabs) => {
    if (tab.isEmpty) return <EmptySection />;

    return tab.render();
  };

  const handleOpenVideoSurveyModal = useCallback(() => {
    const tabsVideoSurvey = tabs.find((tab) => tab.key === Navigation.VIDEO_SURVEY);
    const element = document.getElementById(`${Navigation.VIDEO_SURVEY}-item`);
    const hasToOpen = localStorage.getItem("hasToOpen");

    if (tabsVideoSurvey) {
      if (
        tabsVideoSurvey.isOpened &&
        element?.classList.contains("active") &&
        hasToOpen === "true"
      ) {
        handleCloseModal(true);
      }
    }
  }, [tabs]);

  const handleCloseModal = (value: boolean) => {
    if (setOpenModal) setOpenModal(value);
  };

  useImperativeHandle(ref, () => ({
    isOpen: openSideBar,
    setOpenSideBar,
    setCurrentLink,
  }));

  useEffect(() => {
    document?.getElementById("componentSection")?.addEventListener("wheel", handleWheel);

    return () => {
      document.getElementById("componentSection")?.removeEventListener("wheel", handleWheel);
    };
  });

  useEffect(() => {
    handleOpenVideoSurveyModal();
  }, [currentLink]);

  useEffect(() => {
    if (openSideBar !== true) setCurrentLink(Navigation.DETAILS);
  }, [openSideBar]);

  return (
    <Drawer
      anchor="right"
      open={openSideBar}
      ModalProps={{
        keepMounted: false,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disablePortal: true,
      }}
      onClose={() => {
        const modalHasToOpen = localStorage.getItem("hasToOpen");

        if (modalHasToOpen !== undefined) localStorage.setItem("hasToOpen", "true");

        setOpenSideBar(false);
        setComponent(undefined);
        if (onClose) onClose();
      }}
      className={classes.wrapper}
    >
      <section
        data-cy="sidebarNavList"
        className={classnames(classes.sectionWrapperNavList, classes.sectionWrapperMenu)}
      >
        <div className={classes.collapseButton}>
          <span className={classes.collapseText}>{translate("navigation")}</span>
        </div>
        <ul className={classes.listWrapper}>
          {navigation.map((menuItem) => (
            <li
              id={`${menuItem.key}-item`}
              data-cy={menuItem.key}
              className={`${menuItem.key === currentLink ? "active" : ""} ${
                isLoading ? classes.disableListItem : ""
              }`}
              key={menuItem.key}
              onClick={() => {
                if (isLoading) return;
                setComponent(undefined);
                setTimeout(() => handleScrollIntoView(menuItem.key), 500);
              }}
            >
              <span>{menuItem.key}</span>
            </li>
          ))}
        </ul>
        <Separator />
        <div className={classes.buttonsWrapper}>
          {buttonsComponent?.map((button, index: number) => (
            <Button
              key={index}
              data-cy={button.buttonLabel}
              className={button.class}
              customColor={button.color}
              tooltip={button.tooltip}
              disabled={isLoading || button.disabled}
              onClick={() => {
                setCurrentLink("");
                if (button.action) {
                  button.action();
                } else if (button.renderComponent) {
                  setComponent(button);
                }
              }}
            >
              {button.icon}
              <span className={classnames(classes.buttonText, button.classLabel)}>
                {button.buttonLabel}
              </span>
            </Button>
          ))}
        </div>
        {activityButton && (
          <>
            <Separator />
            <Button
              data-cy={activityButton.buttonLabel}
              className={activityButton.class}
              customColor={activityButton.color}
              tooltip={activityButton.tooltip}
              disabled={isLoading || activityButton.disabled}
              onClick={() => {
                setCurrentLink("");
                if (activityButton.action) {
                  activityButton.action();
                } else if (activityButton.renderComponent) {
                  setComponent(activityButton);
                }
              }}
            >
              {activityButton.icon}
              <span className={classnames(classes.buttonText, activityButton.classLabel)}>
                {activityButton.buttonLabel}
              </span>
            </Button>
          </>
        )}
      </section>
      <section
        data-cy="sidebarPage"
        className={classnames(classes.sectionWrapper, classes.sectionWrapperView)}
      >
        <header className={classes.headerView}>
          <span data-cy="sidebarTitle" className={classes.headerViewTitle}>
            {title}
          </span>
          <IconButton
            data-cy="iconButtonClose"
            className={classes.iconButtonClose}
            onClick={() => {
              setOpenSideBar(false);
              setComponent(undefined);
              if (onClose) onClose();
            }}
          >
            <RiCloseLine color="#308CDB" />
          </IconButton>
        </header>
        <section id="componentSection" className={classes.componentSection}>
          {component && (
            <div data-cy={`component-${component.titlePage}`}>
              <p data-cy="componentTitlePage" className={classes.componentTitle}>
                {component.titlePage}
              </p>
              <div data-cy="componentPage" className={classes.componentItem}>
                {isLoading
                  ? component.renderLoading && component.renderLoading()
                  : component.renderComponent?.()}
              </div>
            </div>
          )}
          {!component &&
            tabs?.map((tab) => (
              <div id={tab.key} key={tab.key}>
                <p data-cy={`page-${tab.key.toLowerCase()}`} className={classes.componentTitle}>
                  {tab.key}
                </p>
                <div data-cy={`tabPage-${tab.key.toLowerCase()}`} className={classes.componentItem}>
                  {tab.loading ? tab.renderLoading && tab.renderLoading() : renderSection(tab)}
                </div>
              </div>
            ))}
        </section>
      </section>
    </Drawer>
  );
};

export default forwardRef(SideViewComponent);
